//
// [アドイン] 個人の持ち出し履歴の全表示と個別削除（サーバー対応）
//
// ./CheckoutHistoryEditModal.js
//

import React, { useEffect, useState } from 'react';
import { useEscapeClose } from '../hooks/useEscapeClose';
import DoubleConfirmModal from './doubleConfirmModal';

function CheckoutHistoryEditModal({ userId, onClose }) {
  const [records, setRecords] = useState([]); // レコード（各レコードは {timestamp,userId,items:[...]}）
  const [userList, setUserList] = useState([]);
  const [selected, setSelected] = useState({}); // 選択チェックボックスの状態
  const [modalConfig, setModalConfig] = useState(null); // 確認・メッセージ用
  const [confirmConfig, setConfirmConfig] = useState(null); // 二段階確認用
  const [loading, setLoading] = useState(false);

  useEscapeClose(onClose); // Esc で閉じる

  useEffect(() => {
    // ユーザー一覧読み込み（表示名取得用）
    fetch("https://newells4610.stars.ne.jp/data/userList.json", { cache: "no-store" })
      .then(res => res.json())
      .then(data => setUserList(data))
      .catch(err => {
        console.error("ユーザーリスト取得エラー", err);
        setUserList([]);
      });
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await fetch("https://newells4610.stars.ne.jp/data/checkoutrecords.json", { cache: "no-store" });
      const all = await res.json();
      // userId マッチのレコードのみ抽出し、日時降順
      const filtered = (all || [])
        .filter(rec => String(rec.userId || rec.user) === String(userId))
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setRecords(filtered);
      setSelected({}); // 選択リセット
    } catch (err) {
      console.error("履歴取得失敗", err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchHistory();
  }, [userId]);

  const user = userList.find(u => String(u.id) === String(userId));
  const displayName = user?.name || userId;

  // 選択トグル
  const toggleSelect = (key) => {
    setSelected(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // 選択をすべてチェック/解除
  const toggleSelectAll = (checked) => {
    if (!checked) {
      setSelected({});
      return;
    }
    const map = {};
    records.forEach(rec => {
      (rec.items || []).forEach(it => {
        const k = `${rec.timestamp}_${it.id ?? ''}_${it.productId ?? ''}_${it.quantity ?? ''}`;
        map[k] = true;
      });
    });
    setSelected(map);
  };

  // 「選択項目を削除」ボタン押下
  const handleDeleteSelected = () => {
    // 行（削除対象）を組み立てる
    const rowsToDelete = [];
    records.forEach(rec => {
      (rec.items || []).forEach(it => {
        const key = `${rec.timestamp}_${it.id ?? ''}_${it.productId ?? ''}_${it.quantity ?? ''}`;
        if (selected[key]) {
          rowsToDelete.push({
            timestamp: rec.timestamp,
            userId: rec.userId || rec.user,
            itemId: it.id ?? null,
            productId: it.productId ?? null,
            quantity: it.quantity ?? null
          });
        }
      });
    });

    if (rowsToDelete.length === 0) {
      setModalConfig({ message: "削除する項目を選択してください。", onConfirm: () => setModalConfig(null) });
      return;
    }

    setConfirmConfig({
      message: `選択した ${rowsToDelete.length} 項目を削除します。本当に実行しますか？`,
      showCancel: true,
      onConfirm: async () => {
        setConfirmConfig(null);
        try {
          setLoading(true);
          const res = await fetch("https://newells4610.stars.ne.jp/api/delete_checkout_items.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ rows: rowsToDelete }),
          });
          const json = await res.json();
          if (!res.ok || json.status !== "ok") {
            throw new Error(json.message || "削除に失敗しました");
          }
          // 再取得して反映
          await fetchHistory();
          setModalConfig({ message: "選択した項目を削除しました。", onConfirm: () => setModalConfig(null) });
        } catch (err) {
          console.error("削除エラー", err);
          setModalConfig({ message: `削除エラー: ${err.message}`, onConfirm: () => setModalConfig(null) });
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => setConfirmConfig(null),
    });
  };

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-box" style={{ maxWidth: 760 }}>
          <h4 style={{ marginBottom: 6 }}>{displayName} さんの持ち出し履歴（編集）</h4>

          <div style={{ marginBottom: 8, fontSize: '0.9em', color: '#444' }}>
            ※チェックを付けて「選択項目を削除」を押すと、そのアイテムだけを履歴から削除します。<br />全データ削除は上位画面の「チェックした人の全履歴削除」を使ってください。
          </div>

          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: '0.9em' }}>
              <input
                type="checkbox"
                onChange={(e) => toggleSelectAll(e.target.checked)}
                checked={
                  Object.keys(selected).length > 0 &&
                  records.every(rec => (rec.items || []).every(it => {
                    const k = `${rec.timestamp}_${it.id ?? ''}_${it.productId ?? ''}_${it.quantity ?? ''}`;
                    return !!selected[k];
                  }))
                }
              /> 全選択・解除
            </label>
          </div>

          <div className="modal-list small-text" style={{ maxHeight: '58vh', overflowY: 'auto', border: '1px solid #ddd', padding: 8 }}>
            {loading && <div>読み込み中...</div>}
            {!loading && records.length === 0 && <p>履歴はありません。</p>}

            {!loading && records.map((rec, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <strong style={{ display: 'block', marginBottom: 6 }}>{new Date(rec.timestamp).toLocaleString()}</strong>
                {(rec.items || []).map((it, j) => {
                  const key = `${rec.timestamp}_${it.id ?? ''}_${it.productId ?? ''}_${it.quantity ?? ''}`;
                  return (
                    <div key={j} style={{ paddingLeft: '0.6em', marginBottom: 4, display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!selected[key]}
                        onChange={() => toggleSelect(key)}
                        style={{ marginRight: 8 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div>{it.name} （{it.productId}）✕ {it.quantity}{it.unit ? (' ' + it.unit) : ''}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="modal-actions" style={{ marginTop: 10 }}>
            <button onClick={handleDeleteSelected} style={{ background: '#faa' }}>
              選択項目を削除
            </button>
            <button onClick={onClose}>
              閉じる
            </button>
          </div>
        </div>
      </div>

      {/* 二段確認モーダル（削除） */}
      {confirmConfig && <DoubleConfirmModal {...confirmConfig} />}

      {/* 汎用メッセージ（OKだけ） */}
      {modalConfig && <DoubleConfirmModal {...modalConfig} />}
    </>
  );
}

export default CheckoutHistoryEditModal;
